import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientOfferViewUnit, RecordedUnitDecisions } from '@ooh/contracts';
import {
  SharedClientOfferContent,
  noopAccept,
  READ_ONLY_ACCEPT_MUTATION,
} from '../client-offer/shared-client-offer-content';
import { buildEnrichedUnitById } from '../client-offer/lib/build-enriched-unit-lookup';
import { buildReadOnlyDecisionsFromRecorded } from '../client-offer/lib/seed-selections-from-prefill';

// ─── Post-acceptance read-only content (client-offer-interactive-view /
// Post-Acceptance Read-Only Client View) ───────────────────────────────────────
// Bypasses useClientOfferSelections entirely — see the identical rationale in
// client-offer-page.tsx's ClientOfferReadOnlyContent.

export interface PublicOfferReadOnlyContentProps {
  versionNumber: number;
  publishedAt: Date;
  acceptedAt: Date | null;
  clientName: string;
  offerTitle: string;
  defaultDateFrom: Date | null;
  defaultDateTo: Date | null;
  units: ClientOfferViewUnit[];
  recordedDecisions: RecordedUnitDecisions | undefined;
  onOpenUnitDetail: (mediaUnitId: number) => void;
}

export function PublicOfferReadOnlyContent({
  versionNumber,
  publishedAt,
  acceptedAt,
  clientName,
  offerTitle,
  defaultDateFrom,
  defaultDateTo,
  units,
  recordedDecisions,
  onOpenUnitDetail,
}: PublicOfferReadOnlyContentProps) {
  const { t, i18n } = useTranslation('offers');
  const enrichedById = useMemo(() => buildEnrichedUnitById(units), [units]);
  const { decisions, selectedPeriods } = useMemo(
    () =>
      buildReadOnlyDecisionsFromRecorded(
        recordedDecisions,
        enrichedById,
        defaultDateFrom,
        defaultDateTo,
        i18n.language,
      ),
    [recordedDecisions, enrichedById, defaultDateFrom, defaultDateTo, i18n.language],
  );

  return (
    <div className="h-screen overflow-y-auto" data-testid="public-offer-scroll-container">
      <SharedClientOfferContent
        versionNumber={versionNumber}
        publishedAt={publishedAt}
        acceptedAt={acceptedAt}
        rejectedAt={null}
        diff={null}
        onAccept={noopAccept}
        acceptMutation={READ_ONLY_ACCEPT_MUTATION}
        clientName={clientName}
        offerTitle={offerTitle}
        readOnly
        finalizedHint={t('clientOfferReviewV2.readOnlyRecordedHint')}
        decisions={decisions}
        selectedPeriods={selectedPeriods}
        snapshotUnits={units}
        enrichedUnitsById={enrichedById}
        showSupplierIdentity={false}
        campaignFrom={defaultDateFrom}
        campaignTo={defaultDateTo}
        accessMode="Link publiczny"
        onOpenUnitDetail={onOpenUnitDetail}
      />
    </div>
  );
}
